import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/config";
import useReveal from "@/hooks/useReveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const EMPTY = { author_name: "", location: "", rating: 5, message: "", email: "" };

export default function Testimonials() {
    const ref = useReveal();
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const [index, setIndex] = useState(0);
    const [showForm, setShowForm] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/comments");
            setComments(r.data || []);
        } catch {
            /* silent */
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.author_name || !form.message || form.message.length < 10) {
            toast.error("Nom et message (10 caractères min.) requis");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/comments", form);
            toast.success("Merci ! Votre témoignage est en attente de validation.");
            setForm(EMPTY);
            setShowForm(false);
        } catch {
            toast.error("Erreur lors de l'envoi. Réessayez.");
        } finally {
            setSubmitting(false);
        }
    };

    const hasComments = comments.length > 0;
    const current = hasComments ? comments[index % comments.length] : null;
    const prev = () => setIndex((i) => (i - 1 + comments.length) % comments.length);
    const next = () => setIndex((i) => (i + 1) % comments.length);

    return (
        <section
            id="temoignages"
            ref={ref}
            data-testid="testimonials-section"
            className="relative py-24 md:py-32 bg-cream"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-10">
                <div className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div>
                        <span className="label-overline text-brand">Témoignages</span>
                        <h2 className="mt-4 font-heading text-3xl md:text-5xl font-light tracking-tighter text-ink leading-tight">
                            Ce que disent <span className="italic">mes clients.</span>
                        </h2>
                    </div>
                    <button
                        onClick={() => setShowForm(!showForm)}
                        data-testid="testimonial-toggle-form"
                        className="inline-flex items-center gap-2 px-5 py-3 border border-border hover:border-brand bg-card text-ink rounded-sm font-medium tracking-tight self-start md:self-end"
                    >
                        {showForm ? "Fermer" : "Laisser un témoignage"}
                    </button>
                </div>

                {/* Public carousel */}
                <div className="reveal">
                    {loading ? (
                        <div className="text-stone2" data-testid="testimonials-loading">Chargement…</div>
                    ) : !hasComments ? (
                        <div
                            data-testid="testimonials-empty"
                            className="bg-card border border-border rounded-sm p-10 text-center"
                        >
                            <Quote className="mx-auto text-brand/40 mb-3" size={28} />
                            <p className="text-stone2">
                                Aucun témoignage publié pour l'instant. Vous pouvez être le premier à partager votre expérience.
                            </p>
                        </div>
                    ) : (
                        <div className="relative bg-card border border-border rounded-sm p-8 md:p-14">
                            <Quote className="absolute top-6 left-6 text-brand/25" size={40} />
                            <div className="pt-6" data-testid={`testimonial-${current.id}`}>
                                <div className="flex gap-1 mb-5" aria-label={`Note ${current.rating}/5`}>
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <Star
                                            key={i}
                                            size={16}
                                            className={i < current.rating ? "fill-brand text-brand" : "text-border"}
                                            strokeWidth={1.5}
                                        />
                                    ))}
                                </div>
                                <p className="font-heading text-xl md:text-2xl text-ink leading-relaxed font-light tracking-tight max-w-3xl">
                                    « {current.message} »
                                </p>
                                <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
                                    <div>
                                        <div className="font-heading text-base font-medium text-ink tracking-tight">
                                            {current.author_name}
                                        </div>
                                        {current.location && (
                                            <div className="text-xs text-stone2 mt-0.5 label-overline">
                                                {current.location}
                                            </div>
                                        )}
                                    </div>
                                    {comments.length > 1 && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-stone2 mr-2">
                                                {(index % comments.length) + 1} / {comments.length}
                                            </span>
                                            <button
                                                onClick={prev}
                                                data-testid="testimonial-prev"
                                                className="w-9 h-9 rounded-sm border border-border hover:border-brand flex items-center justify-center"
                                                aria-label="Précédent"
                                            >
                                                <ChevronLeft size={16} />
                                            </button>
                                            <button
                                                onClick={next}
                                                data-testid="testimonial-next"
                                                className="w-9 h-9 rounded-sm border border-border hover:border-brand flex items-center justify-center"
                                                aria-label="Suivant"
                                            >
                                                <ChevronRight size={16} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Submit form */}
                {showForm && (
                    <form
                        onSubmit={submit}
                        data-testid="testimonial-form"
                        className="mt-8 bg-card border border-border rounded-sm p-6 md:p-10 space-y-5 animate-fade-up"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Field label="Votre nom" required>
                                <Input
                                    data-testid="testimonial-name"
                                    value={form.author_name}
                                    onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                                    placeholder="Jean Dupont"
                                    required
                                />
                            </Field>
                            <Field label="Ville (facultatif)">
                                <Input
                                    data-testid="testimonial-location"
                                    value={form.location}
                                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                                    placeholder="Chartres"
                                />
                            </Field>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Field label="Email (privé)">
                                <Input
                                    type="email"
                                    data-testid="testimonial-email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    placeholder="vous@email.fr"
                                />
                            </Field>
                            <Field label="Note">
                                <div className="flex items-center gap-1.5 h-10" data-testid="testimonial-rating">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => setForm({ ...form, rating: n })}
                                            data-testid={`testimonial-rating-${n}`}
                                            className="p-1"
                                            aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
                                        >
                                            <Star
                                                size={22}
                                                className={n <= form.rating ? "fill-brand text-brand" : "text-border"}
                                                strokeWidth={1.5}
                                            />
                                        </button>
                                    ))}
                                </div>
                            </Field>
                        </div>
                        <Field label="Votre témoignage" required>
                            <Textarea
                                data-testid="testimonial-message"
                                rows={5}
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                placeholder="Racontez votre expérience avec l'Atelier du Bâti Ancien…"
                                required
                            />
                        </Field>
                        <p className="text-xs text-stone2">
                            Votre témoignage sera relu avant publication. Votre email n'est jamais affiché.
                        </p>
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="testimonial-submit"
                                className="px-6 py-3 bg-brand hover:bg-brand-hover disabled:opacity-60 text-cream rounded-sm font-medium tracking-tight"
                            >
                                {submitting ? "Envoi…" : "Envoyer mon témoignage"}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowForm(false)}
                                className="px-6 py-3 border border-border rounded-sm hover:bg-sand/40"
                            >
                                Annuler
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </section>
    );
}

function Field({ label, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-medium text-ink/80 uppercase tracking-wider">
                {label} {required && <span className="text-brand">*</span>}
            </Label>
            {children}
        </div>
    );
}
